import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./SearchModal.css";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import { AIRPORTS } from "../constants";
import { getAirlines } from "../airlineRegistry";

const SEARCH_HISTORY_KEY = "renuairhub_search_history";
const MAX_HISTORY_ITEMS = 10;

function SearchModal({
  isOpen,
  onClose,
  onSearch,
  error,
  onClearError,
  flightsData,
  regionalFlightsData,
}) {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);

  // Issue #2: Load search history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Failed to load search history:", e);
      }
    }
  }, []);

  // Issue #2: Save search to history
  const saveToHistory = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setSearchHistory((prev) => {
      // Remove duplicate if exists
      const filtered = prev.filter(
        (item) => item.toLowerCase() !== trimmedQuery.toLowerCase(),
      );
      // Add to beginning
      const newHistory = [trimmedQuery, ...filtered].slice(
        0,
        MAX_HISTORY_ITEMS,
      );
      // Save to localStorage
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Issue #2: Clear search history
  const clearHistory = useCallback(() => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
    if (onClearError) {
      onClearError();
    }
  }, [onClearError]);

  // Issue #5: Use regionalFlightsData for better autocomplete
  const dataSource =
    regionalFlightsData &&
    (regionalFlightsData.departures.length > 0 ||
      regionalFlightsData.arrivals.length > 0)
      ? regionalFlightsData
      : flightsData;

  // Generate autocomplete suggestions based on search query
  const suggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    const results = [];

    // Issue #5: Get unique flight numbers from regionalFlightsData (more comprehensive)
    if (dataSource) {
      const allFlights = [
        ...(dataSource.departures || []),
        ...(dataSource.arrivals || []),
      ];
      const flightNumbers = new Set();

      allFlights.forEach((flight) => {
        const flightNumber = flight.flight?.iata;
        if (
          flightNumber &&
          flightNumber.toLowerCase().includes(query) &&
          !flightNumbers.has(flightNumber)
        ) {
          flightNumbers.add(flightNumber);
          results.push({
            type: "flight",
            value: flightNumber,
            label: `${t("search.typeFlight")} ${flightNumber}`,
          });
        }
      });
    }

    // Add tracked cities (show in both languages)
    AIRPORTS.forEach((airport) => {
      const cityName = airport.city?.split(",")[0]?.trim();
      if (cityName && cityName.toLowerCase().includes(query)) {
        results.push({
          type: "city",
          value: cityName,
          label: `${cityName} (${airport.iataCode})`,
        });
      }
    });

    // Add airline suggestions (by name or code)
    getAirlines().forEach((airline) => {
      const nameMatch = airline.name.toLowerCase().includes(query);
      const iataMatch = airline.iata.toLowerCase().includes(query);
      const icaoMatch = airline.icao.toLowerCase().includes(query);

      if (nameMatch || iataMatch || icaoMatch) {
        results.push({
          type: "airline",
          value: airline.name,
          label: `${airline.name} (${airline.iata})`,
        });
      }
    });

    // Add tracked countries (show in current language)
    const countries = [
      {
        en: "Ghana",
        fr: "Ghana",
        code: "ACC",
        city: "Accra",
      },
      {
        en: "Guinea",
        fr: "Guinée",
        code: "CKY",
        city: "Conakry",
      },
      {
        en: "Ivory Coast",
        fr: "Côte d'Ivoire",
        code: "ABJ",
        city: "Abidjan",
      },
      {
        en: "Nigeria",
        fr: "Nigéria",
        code: "LOS",
        city: "Lagos",
      },
      {
        en: "Senegal",
        fr: "Sénégal",
        code: "DSS",
        city: "Dakar",
      },
      {
        en: "Mali",
        fr: "Mali",
        code: "BKO",
        city: "Bamako",
      },
    ];

    countries.forEach((country) => {
      const currentLangCountry =
        i18n.language === "fr" ? country.fr : country.en;
      const otherLangCountry = i18n.language === "fr" ? country.en : country.fr;

      // Check if query matches in either language
      if (
        currentLangCountry.toLowerCase().includes(query) ||
        otherLangCountry.toLowerCase().includes(query) ||
        country.fr.toLowerCase().replace("é", "e").includes(query) ||
        country.city.toLowerCase().includes(query)
      ) {
        // Add country suggestion in current language
        if (!results.find((r) => r.value === currentLangCountry)) {
          results.push({
            type: "country",
            value: currentLangCountry,
            label: `${currentLangCountry} (${country.code})`,
          });
        }
      }
    });

    // Limit to 8 suggestions
    return results.slice(0, 8);
  }, [searchQuery, dataSource, t, i18n.language]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      setShowSuggestions(true);
      // Clear error when user types
      if (onClearError) {
        onClearError();
      }
    },
    [onClearError],
  );

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      setSearchQuery(suggestion.value);
      setShowSuggestions(false);
      // Issue #2: Save to history before searching
      saveToHistory(suggestion.value);
      // Automatically trigger search when suggestion is clicked
      onSearch(suggestion.value);
      setSearchQuery(""); // Clear after search
    },
    [onSearch, saveToHistory],
  );

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      // Issue #2: Save to history before searching
      saveToHistory(searchQuery.trim());
      onSearch(searchQuery.trim());
      setSearchQuery(""); // Clear search input after search
      setShowSuggestions(false);
    }
  }, [searchQuery, onSearch, saveToHistory]);

  // Issue #2: Handle clicking on history item
  const handleHistoryClick = useCallback(
    (historyItem) => {
      setSearchQuery(historyItem);
      setShowSuggestions(false);
      onSearch(historyItem);
      setSearchQuery(""); // Clear after search
    },
    [onSearch],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && searchQuery.trim()) {
        handleSearch();
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [handleSearch, searchQuery],
  );

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setShowSuggestions(false);
    if (onClearError) {
      onClearError();
    }
    onClose();
  }, [onClose, onClearError]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setShowSuggestions(false);
    if (onClearError) {
      onClearError();
    }
  }, [onClearError]);

  if (!isOpen) return null;

  return (
    <div className="search-modal-overlay" onClick={handleClose}>
      <div
        className="search-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-modal-header">
          <h2>{t("search.title")}</h2>
          <button className="search-modal-close" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="search-modal-body">
          {error && <div className="search-error-message">{error}</div>}

          {/* Issue #2: Show recent searches when no query */}
          {!searchQuery && searchHistory.length > 0 && (
            <div className="search-history">
              <div className="search-history-header">
                <span>{t("search.recentSearches")}</span>
                <button
                  className="search-history-clear"
                  onClick={clearHistory}
                  aria-label="Clear history"
                >
                  {t("search.clearHistory")}
                </button>
              </div>
              <div className="search-history-items">
                {searchHistory.map((item, index) => (
                  <button
                    key={`history-${index}`}
                    className="search-history-item"
                    onClick={() => handleHistoryClick(item)}
                  >
                    <HistoryIcon fontSize="small" />
                    <span>{item}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="search-input-container">
            <SearchIcon className="search-input-icon" />
            <input
              type="text"
              className="search-input"
              placeholder={t("search.placeholder")}
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(true)}
              autoFocus
              autoComplete="off"
            />
            {searchQuery && (
              <button
                className="search-clear-button"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.type}-${suggestion.value}-${index}`}
                    className="search-suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <span className="suggestion-type">
                      {t(
                        `search.type${suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)}`,
                      )}
                    </span>
                    <span className="suggestion-label">{suggestion.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={!searchQuery.trim()}
          >
            {t("search.searchButton")}
          </button>
        </div>
        <div className="search-modal-footer">
          <p className="search-hint">{t("search.examples")}</p>
        </div>
      </div>
    </div>
  );
}

export default SearchModal;
